import React from 'react';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react';
import store from '../../../../s';
import {currency, isNullOrWhiteSpace, ep_content_to_lines} from '../../../../../util';
import {ICartLineItem, ICheckoutStep} from '../../../../i';
import {formatCartItemCount, getCartItemTotal} from '../../cart-quantity/cart-quantity';
import {showsFastShippingBadge} from '../../fast-shipping-badge';

const hasText = (value?: string) => !!value && value.trim().length > 1;

const SummaryItemDetails = ({o}: {o: ICartLineItem}) => {
    const epLines = [o.ep1, o.ep2, o.ep3, o.ep4, o.ep5, o.ep6, o.ep7, o.ep8, o.ep9, o.ep10].filter(Boolean);

    return (
        <div className="cs-r-item-details">
            {o.details.map((d, i) => (
                <div
                    key={i}
                    dangerouslySetInnerHTML={{__html: d}}
                />
            ))}
            {o.hasEp && (
                <div>
                    <div className="cs-r-item-detail-title">{o.epTitle}</div>
                    {epLines.map((line, i) => (
                        <div key={i}>{line}</div>
                    ))}
                    {o.epMethodId == 3 && (
                        <ul>
                            {ep_content_to_lines(o.epContent).map((line, i) => (
                                <li key={i}>
                                    Line {i + 1}: {line}
                                </li>
                            ))}
                        </ul>
                    )}
                    {o.epWidth && o.epHeight && (
                        <div>
                            <span className="cs-r-item-detail-title">Plate Size:</span> {o.epWidth}"w x {o.epHeight}"h
                        </div>
                    )}
                    {!!o.ep2Width && (
                        <div>
                            <span className="cs-r-item-detail-title">Second plate:</span> {o.ep2Width}"w x {o.ep2Height}"h
                        </div>
                    )}
                </div>
            )}
            {o.hasCustom && (
                <div>
                    {/* A product keeps its custom-field titles after the field is turned off (the INFORMS awards moved
                        to a UV template), and an optional field may be left empty: a row needs an entry, as the cart step. */}
                    {hasText(o.CustomField1_Title) && hasText(o.CustomField1) && (
                        <div>
                            <span className="cs-r-item-detail-title">{o.CustomField1_Title}:</span> {o.CustomField1}
                        </div>
                    )}
                    {hasText(o.CustomField2_Title) && hasText(o.CustomField2) && (
                        <div>
                            <span className="cs-r-item-detail-title">{o.CustomField2_Title}:</span> {o.CustomField2}
                        </div>
                    )}
                    {hasText(o.CustomField3_Title) && hasText(o.CustomField3) && (
                        <div>
                            <span className="cs-r-item-detail-title">{o.CustomField3_Title}:</span> {o.CustomField3}
                        </div>
                    )}
                </div>
            )}
            {!o.IsDYF && o.gradYear > 0 && <div>Grad Year: {o.gradYear.toString()}</div>}
        </div>
    );
};

const SummaryItem = observer(({o, idPrefix}: {o: ICartLineItem; idPrefix: string}) => {
    // The stacked layout renders the same line in the top collapsible and in the detail block, so the id carries the instance.
    const detailsId = `cs-r-details-${idPrefix}-${o.id}`;
    const showDetails = !!o._showingDetail;
    // The cart step's badge follows the line into the summary, so a fast-shipping product keeps looking like one after "Proceed to checkout".
    const fastShipping = showsFastShippingBadge(o, store.checkout.isMyFrames);

    return (
        <li className="cs-r-item">
            <a
                href={o.URL}
                className="cs-r-item-img"
                tabIndex={-1}
                aria-hidden={true}
            >
                <img
                    src={o.imgThumb.replace('_200_200.png', '_400_400.png').replace('images.d', 'images2.d').replace('com//', 'com/')}
                    width={200}
                    height={200}
                    alt=""
                    loading="lazy"
                />
                {fastShipping && <span className="cs-r-fast-ship">Fast shipping</span>}
            </a>
            <div className="cs-r-item-body">
                <a
                    href={o.URL}
                    className="cs-r-item-title"
                    aria-label={`View Cart Product ${o.title1}`}
                >
                    {o.title1}
                </a>
                <div className="cs-r-item-desc">{o.title2}</div>
                <div className="cs-r-item-price">
                    {o.qty > 1 ? `${o.qty} x ` : ''}
                    {currency(o.price)}
                </div>
                {fastShipping && <div className="ab-sr-only">Fast shipping</div>}
                {o.VolumeDiscountAmt > 0 && (
                    <div className="cs-r-item-note">
                        Saving <b>{currency(o.VolumeDiscountAmt)}</b> with {o.VolumeDiscountPercent}% volume discount
                    </div>
                )}
                {o.discountHighlight !== '' && (
                    <div
                        className="cs-r-item-note"
                        dangerouslySetInnerHTML={{__html: o.discountHighlight}}
                    />
                )}
                {!isNullOrWhiteSpace(o.special_msg) && <div className="cs-r-item-note">{o.special_msg}</div>}
                {o.productId !== 24791 && (
                    <button
                        type="button"
                        className="cs-r-item-toggle"
                        aria-expanded={showDetails}
                        aria-controls={detailsId}
                        onClick={() =>
                            runInAction(() => {
                                o._showingDetail = !o._showingDetail;
                            })
                        }
                    >
                        {showDetails ? 'Hide details' : 'Expand details'}
                        <i
                            className={`fas fa-chevron-${showDetails ? 'up' : 'down'}`}
                            aria-hidden={true}
                        />
                    </button>
                )}
            </div>
            {o.productId !== 24791 && (
                <div
                    id={detailsId}
                    className="cs-r-item-details-slot"
                    hidden={!showDetails}
                >
                    {showDetails && <SummaryItemDetails o={o} />}
                </div>
            )}
        </li>
    );
});

const SummaryCartItems = observer(({idPrefix}: {idPrefix: string}) => {
    const items = store.checkout.cart.items;
    const canEdit = !store.uiCheckout.processing;

    return (
        <section
            className="cs-r-cart"
            aria-label="Shopping cart"
        >
            <div className="cs-r-cart-header">
                <h2 className="cs-r-cart-title">Shopping cart</h2>
                <span className="cs-r-cart-count">{formatCartItemCount(getCartItemTotal(items))}</span>
                {canEdit && (
                    <button
                        type="button"
                        className="cs-r-small-btn cs-r-edit"
                        onClick={() => store.moveStep(ICheckoutStep.cart)}
                        aria-label="Edit cart"
                    >
                        <i
                            className="far fa-pen"
                            aria-hidden={true}
                        />
                        Edit
                    </button>
                )}
            </div>
            <ul className="cs-r-items">
                {items.map(o => (
                    <SummaryItem
                        key={o.id}
                        o={o}
                        idPrefix={idPrefix}
                    />
                ))}
            </ul>
        </section>
    );
});

export default SummaryCartItems;
